/**
 * IconPicker - Icon selection grid for categories (WP-CAT-12, WP-CAT-13)
 *
 * Grid of category-appropriate icons for selection.
 * Uses SVG icons via CategoryIcon component (not emojis).
 *
 * Accessibility:
 * - Each icon button has aria-label with icon name
 * - Selected state indicated via aria-pressed
 * - Keyboard navigation: Arrow keys move between icons
 * - Focus ring visible on all icon buttons
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';
import {
  CategoryIcon,
  SUPPORTED_ICON_NAMES,
  ICON_DISPLAY_NAMES,
  type CategoryIconName,
} from '@/components/categories';

// ============================================================================
// Component Props
// ============================================================================

export interface IconPickerProps {
  /** Currently selected icon name (or legacy emoji) */
  value: string;
  /** Callback when icon is selected */
  onChange: (icon: string) => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * IconPicker component - Grid of SVG icons for selection.
 */
export default function IconPicker({ value, onChange, className }: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle icon selection
  const handleSelect = useCallback(
    (iconName: string) => {
      onChange(iconName);
      setIsOpen(false);
    },
    [onChange]
  );

  // Handle clear
  const handleClear = useCallback(() => {
    onChange('');
    setIsOpen(false);
  }, [onChange]);

  // Handle keyboard navigation within the grid
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, iconName: string, index: number) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect(iconName);
      } else if (event.key === 'Escape') {
        setIsOpen(false);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = (index + 1) % SUPPORTED_ICON_NAMES.length;
        focusIconAtIndex(nextIndex);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const prevIndex = (index - 1 + SUPPORTED_ICON_NAMES.length) % SUPPORTED_ICON_NAMES.length;
        focusIconAtIndex(prevIndex);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        // Move down one row (6 columns on medium screens)
        const nextIndex = Math.min(index + 6, SUPPORTED_ICON_NAMES.length - 1);
        focusIconAtIndex(nextIndex);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        // Move up one row
        const prevIndex = Math.max(index - 6, 0);
        focusIconAtIndex(prevIndex);
      }
    },
    [handleSelect]
  );

  // Focus icon button at index
  const focusIconAtIndex = (index: number) => {
    if (gridRef.current) {
      const buttons = gridRef.current.querySelectorAll('button[role="option"]');
      const button = buttons[index] as HTMLButtonElement;
      if (button) {
        button.focus();
      }
    }
  };

  // Get display name for current value
  const getDisplayName = (iconValue: string): string => {
    if (!iconValue) return '';
    // Check if it's a known icon name
    if (ICON_DISPLAY_NAMES[iconValue as CategoryIconName]) {
      return ICON_DISPLAY_NAMES[iconValue as CategoryIconName];
    }
    // Fallback for legacy emoji values
    return 'Selected';
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 border rounded-lg w-full',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          'hover:bg-gray-50 transition-colors',
          isOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-300'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {value ? (
          <>
            <CategoryIcon name={value} size="md" className="text-gray-700" />
            <span className="text-sm text-gray-700">{getDisplayName(value)}</span>
          </>
        ) : (
          <span className="text-sm text-gray-500">Select an icon...</span>
        )}
        <svg
          className={cn('w-4 h-4 ml-auto text-gray-400 transition-transform', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          role="listbox"
          aria-label="Select an icon"
        >
          {/* Clear button */}
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="mb-2 w-full text-left text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
            >
              Clear selection
            </button>
          )}

          {/* Icon grid */}
          <div
            ref={gridRef}
            className="grid grid-cols-6 gap-1"
            role="grid"
          >
            {SUPPORTED_ICON_NAMES.map((iconName, index) => (
              <button
                key={iconName}
                type="button"
                onClick={() => handleSelect(iconName)}
                onKeyDown={(e) => handleKeyDown(e, iconName, index)}
                className={cn(
                  'p-2 rounded-md transition-colors flex items-center justify-center',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500',
                  'hover:bg-gray-100',
                  value === iconName
                    ? 'bg-primary-100 ring-2 ring-primary-500'
                    : 'bg-white'
                )}
                role="option"
                aria-selected={value === iconName}
                aria-label={ICON_DISPLAY_NAMES[iconName]}
                title={ICON_DISPLAY_NAMES[iconName]}
              >
                <CategoryIcon name={iconName} size="lg" className="text-gray-700" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
